import { useIsTemplateQualityGateKilled } from '@/features/flags/template-quality-gate-flag';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { trackTemplateQualityGate } from '@/models/analytics/features/trackTemplateQualityGateAnalytics';
import { logger } from '@/utils/logger/logger';
import {
  templateQualityGateResolverAtom,
  type TemplateQualityGate,
  type TemplateQualityGateContext,
  type TemplateQualityGateDecision,
  type TemplateQualityGateRegionStatus,
  type TemplateQualityGateSurface,
} from '@heidi/templates';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

/**
 * Gate for the template quality attestation (SWR-185).
 *
 * The attestation is required on **every** save that creates a bespoke
 * template, not once per clinician. The requirement reads "on click of save for
 * a generated template", and a once-per-clinician variant was considered and
 * rejected: it weakens an approved risk control, because a clinician who
 * attested months ago never re-confirms that *this* template was reviewed.
 * There is therefore no persisted acceptance state to read or write — the
 * decision is per-save and lives entirely in this session.
 *
 * `requireAcceptance` resolves through a Jotai-held resolver rather than
 * returning control to the caller, so the save that triggered it stays
 * suspended mid-flight — callers that await the created template id (the Share
 * button) keep working unchanged.
 *
 * The attestation is only mandated in the regulated UK/EU markets, so the gate
 * is region-scoped on top of the kill switch. `useIsUKOrEU` reads the
 * authoritative Kinde region — never `getRegion()`, which fails open to AU and
 * honours the spoofable `?region=` param, and so must not gate a
 * medical-device decision. Its pending/errored state is deliberately consumed
 * as "in region": the gate runs inside a save the clinician just triggered, and
 * an unresolved region must prompt rather than wave the save through.
 *
 * `surface` is per hook instance rather than per call because one component
 * owns one entry point; the template id varies save to save and is passed in.
 */
export const useTemplateQualityGate = (
  surface: TemplateQualityGateSurface
): TemplateQualityGate => {
  const { isUKOrEU, isPending, isError, region } = useIsUKOrEU();
  // Read unconditionally: `&&` on the hook call itself would skip it whenever
  // the region check is false and break the hook order between renders.
  const isKilled = useIsTemplateQualityGateKilled();
  const isInScope = isUKOrEU;
  const isEnabled = isInScope && !isKilled;
  const setResolver = useSetAtom(templateQualityGateResolverAtom);

  const regionStatus: TemplateQualityGateRegionStatus = isError
    ? 'error'
    : isPending
      ? 'pending'
      : 'resolved';

  const context = useMemo<TemplateQualityGateContext>(
    () => ({ surface, userRegion: region, regionStatus, isKillSwitchOn: isKilled }),
    [surface, region, regionStatus, isKilled]
  );

  const requireAcceptance = useCallback(
    (templateId?: string) =>
      new Promise<TemplateQualityGateDecision>((resolve) => {
        setResolver((pending) => {
          if (pending) {
            // A second save must never orphan the first: unwind it instead of
            // leaving its promise — and the save awaiting it — pending
            // forever. `superseded`, not `declined`: nobody declined anything.
            //
            // Warned because it destroys a clinician's in-flight save, and
            // Datadog is where support looks when someone reports one
            // vanishing. The event itself is emitted by the unwound save's own
            // `resolveTemplateQualityGate`, which holds its context.
            logger.warn('template_quality_gate_save_superseded', {
              surface: pending.context.surface,
              templateId: pending.context.templateId,
            });
            pending.resolve('superseded');
          }
          return {
            resolve,
            context: { ...context, templateId },
            track: trackTemplateQualityGate,
          };
        });
      }),
    [setResolver, context]
  );

  // Stable identity so callers can list the gate as one dependency.
  return useMemo(
    () => ({
      isEnabled,
      isInScope,
      context,
      requireAcceptance,
      track: trackTemplateQualityGate,
    }),
    [isEnabled, isInScope, context, requireAcceptance]
  );
};
